<template>
  <audio id="alarmAudio" ref="sound">
    <source :src="accountStore.baseUrl + '/assets/audio/alarm.mp3'" />
    <source :src="accountStore.baseUrl + '/assets/audio/alarm.ogg'" />
  </audio>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import _ from 'underscore';
import { storeToRefs } from 'pinia';
import { gsap } from 'gsap';
import { usePhaseStore } from '@/store/pinia/phase';
import { useAccountStore } from '@/store/pinia/account';
import { useTimeStore } from '@/store/pinia/time';
import { useMeetingStore } from '@/store/pinia/meeting';
import { useUsersStore } from '@/store/pinia/users';
import { usePageStore } from '@/store/pinia/page';
const phaseStore = usePhaseStore();
const accountStore = useAccountStore();
const timeStore = useTimeStore();
const meetingStore = useMeetingStore();
const usersStore = useUsersStore();
const pageStore = usePageStore();

let alarmTimeout = null;
const running = ref(false);
const volume = ref(0.6);
const sound = ref(null);

const { currentPhase, breakEndedEarly, groupsReassemble, isAnimating, groupsOverlayShowing, settingUp } = storeToRefs(phaseStore);
const { iAmSpeaking, iAmInWaitingRoom, iAmAway, iAmStillAway, iAmStillAwayInGroup, iAmAwayInGroup } = storeToRefs(usersStore);
const { phaseLength, phaseRemaining, alarmRunning } = storeToRefs(timeStore);
const { currentPageName, isMeeting } = storeToRefs(pageStore);
const { initTitleCardShown } = storeToRefs(meetingStore);
const phaseOverTime = computed(() => {
  if (phaseLength.value === 0) {
    return false;
  } else if (phaseRemaining.value <= 0) {
    return true;
  } else {
    return false;
  }
});
const noAlarm = computed(() => {
  if (!isMeeting.value) {
    return true;
  }
  if (['lobby', 'setTime', 'freeform'].includes(currentPhase.value) || iAmInWaitingRoom.value) {
    return true;
  }
  if (currentPageName.value === 'stopSharingDrawer') {
    return false;
  }
  if (!initTitleCardShown.value) {
    return true;
  }
  return false;
});

const shouldIRunAlarm = computed(() => {
  if (noAlarm.value || isAnimating.value || settingUp.value || groupsOverlayShowing.value) {
    return false;
  } else if (phaseOverTime.value && (iAmSpeaking.value || currentPhase.value === 'endMeeting' || iAmAway.value || iAmAwayInGroup.value)) {
    return true;
  } else if (breakEndedEarly.value && iAmAway.value) {
    return true;
  } else if (iAmStillAway.value || iAmStillAwayInGroup.value) {
    return true;
  } else if (groupsReassemble.value && iAmAwayInGroup.value) {
    return true;
  } else {
    return false;
  }
});

const alarmBroadcast = _.debounce((nv) => {
  if (nv) {
    timeStore.startAlarm();
  } else {
    timeStore.stopAlarm();
  }
}, 500);

watch(shouldIRunAlarm, alarmBroadcast);
watch(noAlarm, () => {
  alarmBroadcast();
});
watch(running, (nv, ov) => {
  if (nv !== ov) {
    if (nv) {
      document.body.classList.add('alarmRunning');
      document.body.classList.remove('alarmNotRunning');
    } else {
      document.body.classList.add('alarmNotRunning');
      document.body.classList.remove('alarmRunning');
    }
  }
});

watch(alarmRunning, (run) => {
  if (run && !running.value) {
    running.value = true;
    start();
  } else if (!run && running.value) {
    stop();
  }
});

onMounted(() => {
  nextTick(() => {
    sound.value.volume = 0;
  });
});

function start() {
  _.delay(run, 200);
}

function run() {
  if (alarmRunning.value) {
    sound.value.play().catch(() => {});
    if (sound.value.volume === 0) {
      gsap.to(sound.value, {
        volume: volume.value,
        duration: 0.2,
      });
    }
    alarmTimeout = setTimeout(() => {
      if (alarmRunning.value) {
        start();
      }
    }, 3000);
  }
}

function stop() {
  running.value = false;
  gsap.fromTo(
    sound.value,
    {
      volume: volume.value,
    },
    {
      volume: 0,
      duration: 0.2,
      onComplete: () => {
        sound.value.pause();
      },
    },
  );
  clearTimeout(alarmTimeout);
}
</script>
